import { useRef } from 'react';
import * as service from '../services';
import { useOmnichannelStore } from '../store/ModuloOmnichannel.store';
import { MAX_PRODUCT_SELECTION } from '../constants';

interface FilterItem {
  value: string | number;
  label: string;
  identificador?: string;
}

type ProductItem = {
  identificador: string;
  descricao: string;
};

const mapToFilterItems = (list: ProductItem[]): FilterItem[] =>
  (list || []).map(item => ({
    label: `${item.identificador} - ${item.descricao}`,
    value: item.identificador,
    identificador: item.identificador,
  }));

const mergeFilterItems = (base: FilterItem[], addition: FilterItem[]): FilterItem[] => {
  const seen = new Set(base.map(item => String(item.value)));
  const merged = [...base];
  addition.forEach(item => {
    if (!seen.has(String(item.value))) {
      seen.add(String(item.value));
      merged.push(item);
    }
  });
  return merged;
};

const buildRequestBody = (query: string, identificadores: string[] | null) => ({
  field: 'produto',
  query,
  identificadores,
  gtin: null,
  cliente: JSON.parse(localStorage.getItem('cliente') || '{}')?.codigo,
});

export const useProductFilter = () => {
  const produto = useOmnichannelStore(state => state.produto);
  const setOmnichannelState = useOmnichannelStore(state => state.setOmnichannelState);

  const buscaPorTextoTimer = useRef<ReturnType<typeof setTimeout>>();
  const bulksPendentes = useRef(0);

  const updateOmnichannelRequestBody = (values: string[]) => {
    const currentBody = useOmnichannelStore.getState().omnichannelRequestBody;
    setOmnichannelState('omnichannelRequestBody', { ...currentBody, gtins: values });
  };

  const updateProductList = (data: ProductItem[], isBulk = false, candidateIdentifiers: string[] | null = null) => {
    const currentProduto = useOmnichannelStore.getState().produto;
    const newList = mapToFilterItems(data);

    if (isBulk) {
      const foundIds = new Set(data.map(item => item.identificador));
      const validCandidates = (candidateIdentifiers || []).filter(id => foundIds.has(id));
      const newValue = [...currentProduto.value, ...validCandidates].slice(0, MAX_PRODUCT_SELECTION);
      const mergedList = mergeFilterItems(currentProduto.cache, newList);

      setOmnichannelState('produto', { ...currentProduto, list: mergedList, cache: mergedList, value: newValue });
      updateOmnichannelRequestBody(newValue);
    } else {
      updateOmnichannelRequestBody(currentProduto.value);

      if (currentProduto.value?.length) {
        const removeDuplicate = newList.filter(item => !currentProduto.value.includes(String(item.value)));
        const concatList = currentProduto.cache.concat(removeDuplicate);
        setOmnichannelState('produto', { ...currentProduto, list: concatList });
      } else {
        setOmnichannelState('produto', { ...currentProduto, list: newList });
      }
    }
  };

  const getProducts = async (body: ReturnType<typeof buildRequestBody>, isBulk = false) => {
    if (isBulk) bulksPendentes.current += 1;
    try {
      const size = isBulk ? body.identificadores?.length || 1 : 50;
      const response = await service.getProduto(body, size);
      const dataList = response?.data ?? response ?? [];

      updateProductList(Array.isArray(dataList) ? dataList : [], isBulk, body.identificadores);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
    } finally {
      if (isBulk) bulksPendentes.current -= 1;
    }
  };

  const onSearchProducts = (inputValue: string) => {
    const state = useOmnichannelStore.getState();
    const value = typeof inputValue === 'string' ? inputValue.trim() : '';
    const isIdentifiers = value.length > 0 && /^\d+(?:\s+\d+)*$/.test(value);

    clearTimeout(buscaPorTextoTimer.current);

    if (isIdentifiers) {
      const existing = state.produto.value;
      const existingSet = new Set(existing);
      const pastedTokens = [...new Set(value.split(/\s+/))].filter(token => !existingSet.has(token));
      const capacity = Math.max(MAX_PRODUCT_SELECTION - existing.length, 0);
      const candidates = pastedTokens.slice(0, capacity);

      if (candidates.length === 0) return;

      const body = buildRequestBody('', candidates);
      getProducts(body, true);
    } else {
      buscaPorTextoTimer.current = setTimeout(() => {
        // o onOpen dispara esta busca ao fim da animação de abertura, depois de uma colagem
        // rápida — buscar agora cancelaria o request dos identificadores colados
        if (bulksPendentes.current > 0) return;
        getProducts(buildRequestBody(value, null), false);
      }, 500);
    }
  };

  const handleSelectProduct = (values: string[]) => {
    const produto = useOmnichannelStore.getState().produto;
    const cappedValues = values.slice(0, MAX_PRODUCT_SELECTION);
    const cache = produto.list.filter(item => cappedValues.includes(String(item.value)));
    setOmnichannelState('produto', { ...produto, value: cappedValues, cache });
    updateOmnichannelRequestBody(cappedValues);
  };

  const handleClearProducts = () => {
    const produto = useOmnichannelStore.getState().produto;
    const omnichannelRequestBody = useOmnichannelStore.getState().omnichannelRequestBody;
    setOmnichannelState('produto', { ...produto, value: [], cache: [] });
    setOmnichannelState('omnichannelRequestBody', { ...omnichannelRequestBody, gtins: [] });
  };

  return {
    produto,
    productHandlers: {
      onSearchProducts,
      handleSelectProduct,
      handleClearProducts,
    },
  };
};
